import React, { useState } from 'react';
import { Plus, Settings, Bell, Target, Filter } from 'lucide-react';
import Button from '../../../components/base/Button';
import Card from '../../../components/base/Card';
import { usePresupuestos } from '../hooks/usePresupuestos';
import { ResumenPresupuestos } from './ResumenPresupuestos';
import { ListaPresupuestos } from './ListaPresupuestos';
import { AlertasPresupuesto } from './AlertasPresupuesto';
import { FormularioPresupuesto } from './FormularioPresupuesto';
import { ConfiguracionAlertas } from './ConfiguracionAlertas';
import type { Presupuesto } from '../../../shared/types/presupuestos';

type Vista = 'resumen' | 'presupuestos' | 'alertas';

export const PresupuestosDashboard: React.FC = () => {
  const {
    presupuestos,
    estadosPresupuesto,
    alertas,
    configuracionAlertas,
    loading,
    error,
    crearPresupuesto,
    actualizarPresupuesto,
    eliminarPresupuesto,
    marcarAlertaLeida,
    actualizarConfiguracionAlertas,
    alertasNoLeidas,
    resumenPresupuestos
  } = usePresupuestos();

  const [vistaActiva, setVistaActiva] = useState<Vista>('resumen');
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [mostrarConfiguracion, setMostrarConfiguracion] = useState(false);
  const [presupuestoEditando, setPresupuestoEditando] = useState<Presupuesto | undefined>();
  const [filtroEstado, setFiltroEstado] = useState<'todos' | 'normal' | 'advertencia' | 'superado'>('todos');

  const estadosFiltrados = React.useMemo(() => {
    if (filtroEstado === 'todos') return estadosPresupuesto;
    return estadosPresupuesto.filter(estado => estado.estado === filtroEstado);
  }, [estadosPresupuesto, filtroEstado]);

  const resumen = resumenPresupuestos();
  const alertasPendientes = alertasNoLeidas();

  const handleNuevoPresupuesto = () => {
    setPresupuestoEditando(undefined);
    setMostrarFormulario(true);
  };

  const handleEditarPresupuesto = (presupuestoId: string) => {
    const presupuesto = presupuestos.find(p => p.id === presupuestoId);
    if (presupuesto) {
      setPresupuestoEditando(presupuesto);
      setMostrarFormulario(true);
    }
  };

  const handleEliminarPresupuesto = async (presupuestoId: string) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este presupuesto?')) {
      try {
        await eliminarPresupuesto(presupuestoId);
      } catch (error) {
        console.error('Error eliminando presupuesto:', error);
      }
    }
  };

  const handleGuardarPresupuesto = async (datos: Omit<Presupuesto, 'id' | 'userId' | 'fechaCreacion' | 'fechaActualizacion'>) => {
    try {
      if (presupuestoEditando) {
        await actualizarPresupuesto(presupuestoEditando.id, datos);
      } else {
        await crearPresupuesto(datos);
      }
      setMostrarFormulario(false);
      setPresupuestoEditando(undefined);
    } catch (error) {
      console.error('Error guardando presupuesto:', error);
    }
  };

  const handleCancelarFormulario = () => {
    setMostrarFormulario(false);
    setPresupuestoEditando(undefined);
  };

  const handleGuardarConfiguracion = async (config: typeof configuracionAlertas) => {
    try {
      await actualizarConfiguracionAlertas(config);
      setMostrarConfiguracion(false);
    } catch (error) {
      console.error('Error guardando configuración:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando presupuestos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6 bg-red-50 border-red-200">
        <p className="text-red-700">Error cargando presupuestos: {error}</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Presupuestos</h1>
          <p className="text-gray-600">Controla tus gastos por categoría</p>
        </div>
        
        <div className="flex space-x-3">
          <Button
            variant="outline"
            onClick={() => setMostrarConfiguracion(true)}
            className="flex items-center"
          >
            <Settings className="w-4 h-4 mr-2" />
            Configuración
          </Button>
          
          <Button
            onClick={handleNuevoPresupuesto}
            className="bg-blue-600 hover:bg-blue-700 text-white flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Presupuesto
          </Button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
        <button
          onClick={() => setVistaActiva('resumen')}
          className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            vistaActiva === 'resumen'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Target className="w-4 h-4 inline mr-2" />
          Resumen
        </button>
        
        <button
          onClick={() => setVistaActiva('presupuestos')}
          className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            vistaActiva === 'presupuestos'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Filter className="w-4 h-4 inline mr-2" />
          Presupuestos ({resumen.total})
        </button>
        
        <button
          onClick={() => setVistaActiva('alertas')}
          className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors relative ${
            vistaActiva === 'alertas'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Bell className="w-4 h-4 inline mr-2" />
          Alertas
          {alertasPendientes.length > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {alertasPendientes.length}
            </span>
          )}
        </button>
      </div>

      {/* Content */}
      {vistaActiva === 'resumen' && (
        <ResumenPresupuestos estados={estadosPresupuesto} />
      )}

      {vistaActiva === 'presupuestos' && (
        <div className="space-y-6">
          {/* Filtros */}
          {estadosPresupuesto.length > 0 && (
            <Card className="p-4">
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-gray-700">Filtrar por estado:</span>
                <select
                  value={filtroEstado}
                  onChange={(e) => setFiltroEstado(e.target.value as typeof filtroEstado)}
                  className="text-sm border border-gray-300 rounded px-3 py-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="todos">Todos ({resumen.total})</option>
                  <option value="normal">Normales ({resumen.normales})</option>
                  <option value="advertencia">Advertencias ({resumen.advertencias})</option>
                  <option value="superado">Superados ({resumen.superados})</option>
                </select>
              </div>
            </Card>
          )}

          <ListaPresupuestos
            estados={estadosFiltrados}
            onEditar={handleEditarPresupuesto}
            onEliminar={handleEliminarPresupuesto}
          />
        </div>
      )}

      {vistaActiva === 'alertas' && (
        <AlertasPresupuesto
          alertas={alertas}
          onMarcarLeida={marcarAlertaLeida}
        />
      )}

      {/* Modales */}
      <FormularioPresupuesto
        presupuesto={presupuestoEditando}
        onGuardar={handleGuardarPresupuesto}
        onCancelar={handleCancelarFormulario}
        isOpen={mostrarFormulario}
      />

      <ConfiguracionAlertas
        configuracion={configuracionAlertas}
        onGuardar={handleGuardarConfiguracion}
        onCancelar={() => setMostrarConfiguracion(false)}
        isOpen={mostrarConfiguracion}
      />
    </div>
  );
};